/**
 * API SERVICE
 * Centralized API calls to shared backend
 * All 4 sites use this same service to fetch/update data
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  try {
    const response = await fetch(`${API_BASE}/api${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });
    
    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`API call failed for ${endpoint}:`, error);
    throw error;
  }
};

// ==================== DOCTOR INFO ====================

export const getDoctorInfo = async () => {
  return apiCall('/doctor');
};

export const updateDoctorInfo = async (data) => {
  return apiCall('/doctor', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

// ==================== BLOG POSTS ====================

export const getBlogPosts = async (category = null) => {
  const params = category ? `?category=${category}` : '';
  return apiCall(`/blog${params}`);
};

export const getBlogPost = async (slug) => {
  return apiCall(`/blog/${slug}`);
};

export const createBlogPost = async (post) => {
  return apiCall('/blog', {
    method: 'POST',
    body: JSON.stringify(post),
  });
};

export const deleteBlogPost = async (postId) => {
  return apiCall(`/blog/${postId}`, {
    method: 'DELETE',
  });
};

// ==================== TESTIMONIALS ====================

export const getTestimonials = async (site = null) => {
  const params = site ? `?site=${site}` : '';
  return apiCall(`/testimonials${params}`);
};

export const createTestimonial = async (testimonial) => {
  return apiCall('/testimonials', {
    method: 'POST',
    body: JSON.stringify(testimonial),
  });
};

export const deleteTestimonial = async (testimonialId) => {
  return apiCall(`/testimonials/${testimonialId}`, {
    method: 'DELETE',
  });
};

// ==================== APPOINTMENTS ====================

export const createAppointment = async (appointment) => {
  return apiCall('/appointments', {
    method: 'POST',
    body: JSON.stringify(appointment),
  });
};

export const getAppointments = async (status = null, sourceSite = null) => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (sourceSite) params.append('source_site', sourceSite);
  const queryString = params.toString() ? `?${params.toString()}` : '';
  return apiCall(`/appointments${queryString}`);
};

export const updateAppointmentStatus = async (appointmentId, status) => {
  return apiCall(`/appointments/${appointmentId}/status?status=${status}`, {
    method: 'PUT',
  });
};

// ==================== CONTACT MESSAGES ====================

export const submitContactMessage = async (message) => {
  return apiCall('/contact', {
    method: 'POST',
    body: JSON.stringify(message),
  });
};

export const getContactMessages = async (read = null) => {
  const params = read !== null ? `?read=${read}` : '';
  return apiCall(`/contact${params}`);
};

// ==================== SEED DATABASE ====================

export const seedDatabase = async () => {
  return apiCall('/seed', {
    method: 'POST',
  });
};

export default {
  getDoctorInfo,
  updateDoctorInfo,
  getBlogPosts,
  getBlogPost,
  createBlogPost,
  deleteBlogPost,
  getTestimonials,
  createTestimonial,
  deleteTestimonial,
  createAppointment,
  getAppointments,
  updateAppointmentStatus,
  submitContactMessage,
  getContactMessages,
  seedDatabase,
};
